import { createAsyncThunk } from "@reduxjs/toolkit";
import { getPostComments } from "../../../firebase/commentsApi";
import { getFirestoreFetchPolicy } from "../../../firebase/firestoreFetchPolicy";
import { fetchUsersByIds } from "../usersCacheSlice.js";
import { writeCache } from "./utils.js";

const COMMENTS_CACHE_PREFIX = "comments_cache:";

export const fetchPostComments = createAsyncThunk(
  "comments/fetchPostComments",
  async (
    { postId, limit = 3, cursor = null, returnCached = false },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const fetchPolicy = getFirestoreFetchPolicy();
      const CACHE_TTL_MS = Number(
        process.env.REACT_APP_COMMENTS_CACHE_TTL_MS || 120000,
      );

      const state = getState();
      const lastAt = state?.comments?.lastFetchedAt?.[postId] || 0;
      const now = Date.now();
      const hasCached =
        Array.isArray(state?.comments?.items?.[postId]) &&
        state.comments.items[postId].length > 0;
      const isFresh = hasCached && now - lastAt < CACHE_TTL_MS;

      if (
        returnCached ||
        (fetchPolicy !== "network-only" && !cursor && isFresh)
      ) {
        return {
          postId,
          comments: [],
          nextCursor: cursor,
          hasMore: state?.comments?.pagination?.[postId]?.hasMore ?? true,
          fromCache: true,
        };
      }

      const comments = await getPostComments(
        postId,
        { pageSize: limit, cursor }
      );

      const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;
      const nextCursor = lastComment
        ? { id: lastComment.id, timestamp: lastComment.timestamp }
        : null;

      // Prefetch user profiles
      const userIds = comments
        .map((c) => c?.userId)
        .filter(Boolean);
      if (userIds.length > 0) {
        dispatch(fetchUsersByIds({ userIds }));
      }

      // Write to cache
      if (!cursor) {
        writeCache(`${COMMENTS_CACHE_PREFIX}${postId}`, comments);
      }

      return {
        postId,
        comments,
        nextCursor,
        hasMore: comments.length === limit,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
